import { memo, useCallback, useState } from "react";
import { type Column, useTable } from "react-table";
import Skeleton from "src/components/Skeleton";
import ThreeDotsWave from "src/components/ThreeDotsWave";
import { ChevronDown } from "src/icons";
import { cn } from "src/utilities/shadcnUtils";

import Touchable from "../Touchable";

export interface TableProps<T = any> {
  columns: Array<Column>;
  data: T[];
  onPressSeeMore?: () => void;
  onClickRow?: (row: T) => void;
  loading?: boolean | number;
  showSeeMore?: boolean;
  cellClassName?: string;
  trClassName?: string;
  getTrClassName?: (row: T) => string | undefined;
  className?: string;
  headerClassName?: string;
  rowClassName?: string;
  tableClassName?: string;
  tableContainerClassName?: string;
}

const STYLES = {
  tableContainer: "relative rounded-md overflow-auto flex flex-1",
  table: "border-collapse w-full",
  touchable: "hover:cursor-pointer",
  header:
    "p-[11px] pl-8 text-left font-['Hanken_Grotesk',sans-serif] font-normal text-xs text-muted-foreground",
  row: "pl-[50px] border-b-[0.5px] border-b-gray-400 border-b-solid",
  cell: "px-8 py-4 text-left font-['Hanken_Grotesk',sans-serif] font-medium whitespace-nowrap",
  seeMoreContainer: "flex justify-center items-center flex-row w-full min-h-[48px] hover:bg-muted",
  icon: "text-muted-foreground h-3 w-3 ml-[5px]",
};

const Table = ({
  columns = [],
  data = [],
  onPressSeeMore,
  onClickRow,
  loading,
  showSeeMore = true,
  cellClassName,
  trClassName,
  getTrClassName,
  className,
  headerClassName,
  rowClassName,
  tableClassName,
  tableContainerClassName,
}: TableProps) => {
  const [seeMoreLoading, setSeeMoreLoading] = useState(false);
  // @ts-expect-error - Column type generic variance issue with react-table
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({ columns, data });

  const handlePressSeeMore = useCallback(async () => {
    setSeeMoreLoading(true);
    await onPressSeeMore?.();
    setSeeMoreLoading(false);
  }, [onPressSeeMore]);

  return (
    <div className={className}>
      <div className={cn(STYLES.tableContainer, tableContainerClassName)}>
        <table className={cn(STYLES.table, tableClassName)} {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr
                {...headerGroup.getHeaderGroupProps()}
                className={cn(STYLES.row, trClassName)}
                key={headerGroup.id}
              >
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps()}
                    className={cn(STYLES.header, cellClassName, headerClassName)}
                    key={column.id}
                  >
                    {/* @ts-ignore */}
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {loading &&
              (typeof loading === "boolean" ? [1, 2] : Array.from(new Array(loading))).map((count) => (
                <tr key={count}>
                  {headerGroups[0].headers.map((column, idx) => (
                    <td
                      className={cn(
                        STYLES.cell,
                        cellClassName,
                        (column as { className?: string })?.className
                      )}
                      key={idx}
                    >
                      <Skeleton height={15} containerClassName="min-w-[150px] w-[60%]" />
                    </td>
                  ))}
                </tr>
              ))}
            {!loading &&
              rows.map((row) => {
                prepareRow(row);
                return (
                  <tr
                    className={cn(
                      "hover:bg-muted",
                      STYLES.row,
                      onClickRow && STYLES.touchable,
                      trClassName,
                      getTrClassName?.(row.original)
                    )}
                    onClick={() => onClickRow?.(row)}
                    {...row.getRowProps()}
                    key={row.id}
                  >
                    {row.cells.map((cell) => {
                      const column = cell.column as {
                        className?: string;
                        getClassName?: (row: any) => string | undefined;
                      };
                      return (
                        <td
                          className={cn(
                            "text-md text-muted-foreground",
                            STYLES.cell,
                            cellClassName,
                            column?.className,
                            column?.getClassName?.(row.original)
                          )}
                          {...cell.getCellProps()}
                          key={cell.column.id}
                        >
                          {/* @ts-ignore */}
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      {showSeeMore && !loading && onPressSeeMore && (
        <Touchable onClick={handlePressSeeMore} className={STYLES.seeMoreContainer}>
          {seeMoreLoading && <ThreeDotsWave />}
          {!seeMoreLoading && (
            <>
              <p className="text-center font-semibold text-muted-foreground text-sm">See more</p>
              <ChevronDown className={STYLES.icon} />
            </>
          )}
        </Touchable>
      )}
    </div>
  );
};

export default memo(Table);
